import { Link } from "react-router-dom";
import { NotebookPen, ArrowRight, GitBranch, FileText, GitGraph, Braces, BookMarked, Terminal, Sparkles } from "lucide-react";

const FEATURES = [
  { icon: FileText, title: "Instant docs", body: "README, architecture, API, database, deployment — generated from the code itself and kept in sync." },
  { icon: GitGraph, title: "UML & Mermaid diagrams", body: "Class, sequence, component and deployment diagrams — editable, reproducible source." },
  { icon: Braces, title: "Pseudocode & explain", body: "Pick a function or module, get plain-English pseudocode and prose that reads like a good engineer wrote it." },
  { icon: BookMarked, title: "Software manuals", body: "Onboarding, ops, security, testing — polished handbooks in the same voice, not scattered notes." },
  { icon: GitBranch, title: "Source ↔ doc traceability", body: "Every generated statement links back to the file, class, or function it came from." },
  { icon: Terminal, title: "Engineering workspace", body: "Three-panel workbench — project explorer, artifact viewer, and a context-aware AI assistant." },
];

const WORKFLOW = [
  { step: "01", title: "Ingest", body: "Upload a zipped repo or connect a Git provider. Knuth walks the tree, filters heavy binaries, and builds an index." },
  { step: "02", title: "Understand", body: "Detects languages, top-level modules, package manifests, and infers architectural boundaries." },
  { step: "03", title: "Generate", body: "One click per artifact, or an entire documentation bundle. Everything editable." },
  { step: "04", title: "Sync", body: "As the repo changes, Knuth flags stale sections and proposes updates — never overwriting your edits without approval." },
];

export default function KnuthLanding() {
  return (
    <div className="container-narrow py-12 md:py-16" data-testid="knuth-landing">
      {/* Hero */}
      <section className="grid lg:grid-cols-5 gap-10 items-start mb-20">
        <div className="lg:col-span-3">
          <div className="label-eyebrow mb-3 inline-flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-teal" /> An app from Durga Programming Adventures
          </div>
          <h1 className="font-heading text-5xl md:text-7xl font-semibold leading-[1.02] mb-5">
            <span className="italic text-marigold">Knuth</span> reads your code.
            <br />Then it writes the docs.
          </h1>
          <p className="text-lg text-ink2 leading-relaxed max-w-xl mb-8">
            Knuth is an AI-powered software documentation and architecture workbench. Give it a codebase — a ZIP, a folder, a Git repository — and get engineering documentation that actually reads like an engineer wrote it.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/knuth" className="btn-primary" data-testid="knuth-launch">
              <NotebookPen className="w-4 h-4" /> Open Workbench <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link to="/knuth/new" className="btn-outline-ink text-sm" data-testid="knuth-new-project">
              Start a new project
            </Link>
          </div>
          <p className="mt-4 text-xs text-ink2">
            Named after <span className="italic">Donald Knuth</span> — because your future self deserves documentation that would make him nod.
          </p>
        </div>

        <div className="lg:col-span-2">
          <div className="rounded-3xl border-2 border-ink bg-ink text-cream p-6 shadow-[0_20px_50px_-15px_rgba(26,27,65,0.55)]" data-testid="knuth-hero-card">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-2.5 h-2.5 rounded-full bg-marigold" />
              <span className="w-2.5 h-2.5 rounded-full bg-saffron" />
              <span className="w-2.5 h-2.5 rounded-full bg-teal" />
              <span className="ml-3 text-xs font-mono text-cream/60">knuth · workbench</span>
            </div>
            <div className="font-mono text-[13px] leading-relaxed space-y-1.5">
              <div className="text-teal">$ knuth analyze ./my-app.zip</div>
              <div className="text-cream/70">→ walking file tree · 214 files</div>
              <div className="text-cream/70">→ detected: Swift, JavaScript, YAML</div>
              <div className="text-cream/70">→ primary language: Swift</div>
              <div className="text-cream/70">→ top modules: App, Systems, Levels</div>
              <div className="text-cream/70">→ manifests: swiftpm, github-actions</div>
              <div className="text-saffron pt-2">✓ project ready · 12 artifacts available</div>
              <div className="text-cream/60 pt-2 text-[11px]">documentation coverage · 0% → 84%</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section className="mb-20" data-testid="knuth-features">
        <div className="mb-8">
          <div className="label-eyebrow mb-2">Capabilities</div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold">Documentation the way engineers actually work.</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f) => (
            <div key={f.title} className="card-soft p-6" data-testid={`knuth-feature-${f.title.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="w-10 h-10 rounded-xl bg-teal/15 text-teal flex items-center justify-center mb-3">
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-1">{f.title}</h3>
              <p className="text-sm text-ink2 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Workflow */}
      <section className="mb-20" data-testid="knuth-workflow">
        <div className="mb-8">
          <div className="label-eyebrow mb-2">Workflow</div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold">Ingest → Understand → Generate → Sync.</h2>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          {WORKFLOW.map((w) => (
            <div key={w.step} className="p-5 rounded-2xl border border-border bg-paper/50" data-testid={`knuth-step-${w.step}`}>
              <div className="font-mono text-xs text-marigold mb-2">{w.step}</div>
              <h3 className="font-heading text-lg font-semibold mb-1">{w.title}</h3>
              <p className="text-sm text-ink2 leading-relaxed">{w.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="card-soft p-10 text-center" data-testid="knuth-cta">
        <div className="w-16 h-16 rounded-2xl bg-teal/15 text-teal flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-8 h-8" />
        </div>
        <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-2">Ready to give Knuth a codebase?</h2>
        <p className="text-ink2 mb-6 max-w-lg mx-auto">Drop a ZIP of any project and watch it map the terrain in seconds. First project is on us.</p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/knuth/new" className="btn-primary" data-testid="knuth-cta-new">
            Start a new project <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <Link to="/knuth" className="btn-outline-ink text-sm">
            Open the workbench
          </Link>
        </div>
      </section>
    </div>
  );
}
